/**
 * Service Worker Utilities
 * Handles unregistering service workers that might interfere with API requests
 */

/**
 * Unregister all service workers and wait for completion
 * Also clears service worker caches to prevent them from re-registering
 * @returns Promise that resolves when all service workers are unregistered
 */
export async function unregisterAllServiceWorkers(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return true; // No service worker support
  }

  try {
    // First, try to get the controlling service worker and update/replace it
    if (navigator.serviceWorker.controller) {
      console.log('🔧 [ServiceWorker] Found controlling service worker, sending skipWaiting message');
      try {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      } catch (e) {
        // Ignore errors
      }
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    
    if (registrations.length === 0) {
      // Still try to clear caches
      await clearServiceWorkerCaches();
      return true; // No service workers registered
    }

    console.log(`🔧 [ServiceWorker] Found ${registrations.length} service worker(s) - unregistering...`);

    // Unregister all service workers
    const unregisterPromises = registrations.map(async (registration) => {
      try {
        // Try to update the service worker first to get the latest version
        try {
          await registration.update();
        } catch (e) {
          // Ignore update errors
        }

        const success = await registration.unregister();
        if (success) {
          console.log('✅ [ServiceWorker] Unregistered:', registration.scope);
        } else {
          console.warn('⚠️ [ServiceWorker] Failed to unregister:', registration.scope);
        }
        return success;
      } catch (error) {
        console.error('❌ [ServiceWorker] Error unregistering:', registration.scope, error);
        return false;
      }
    });

    const results = await Promise.all(unregisterPromises);
    const allUnregistered = results.every(result => result === true);

    // Clear all caches after unregistering
    await clearServiceWorkerCaches();

    if (allUnregistered) {
      console.log('✅ [ServiceWorker] All service workers unregistered and caches cleared');
    } else {
      console.warn('⚠️ [ServiceWorker] Some service workers failed to unregister');
    }

    // Wait a bit for the service worker to fully stop intercepting requests
    await new Promise(resolve => setTimeout(resolve, 200));

    // Double-check that no service workers are still registered
    const finalCheck = await navigator.serviceWorker.getRegistrations();
    if (finalCheck.length > 0) {
      console.warn('⚠️ [ServiceWorker] Some service workers still registered after unregistration:', finalCheck.length);
      // Try one more time
      await Promise.all(finalCheck.map(reg => reg.unregister()));
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return allUnregistered;
  } catch (error) {
    console.error('❌ [ServiceWorker] Error getting registrations:', error);
    // Still try to clear caches even if unregistration fails
    await clearServiceWorkerCaches().catch(() => {});
    return false;
  }
}

/**
 * Clear all service worker caches
 */
async function clearServiceWorkerCaches(): Promise<void> {
  if (!('caches' in window)) {
    return;
  }

  try {
    const cacheNames = await caches.keys();
    console.log(`🔧 [ServiceWorker] Found ${cacheNames.length} cache(s) to clear`);
    
    const deletePromises = cacheNames.map(async (cacheName) => {
      try {
        const deleted = await caches.delete(cacheName);
        if (deleted) {
          console.log('✅ [ServiceWorker] Deleted cache:', cacheName);
        }
        return deleted;
      } catch (error) {
        console.warn('⚠️ [ServiceWorker] Failed to delete cache:', cacheName, error);
        return false;
      }
    });

    await Promise.all(deletePromises);
    console.log('✅ [ServiceWorker] All caches cleared');
  } catch (error) {
    console.error('❌ [ServiceWorker] Error clearing caches:', error);
  }
}

/**
 * Check if any service workers are currently registered
 */
export async function hasActiveServiceWorkers(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    return registrations.length > 0;
  } catch (error) {
    console.error('❌ [ServiceWorker] Error checking registrations:', error);
    return false;
  }
}

